import React from 'react';
import styled from 'styled-components';
import CohortRowTitle from './CohortRowTitle';
import {
  ICohortRow,
  IRowMeta,
  RowType,
} from '../../../../../redux/slices/oldBuilder';
import CohortRowSimpleTypeKey from './CohortRowSimpleTypeKey';
import CohortRowDateRange from './CohortRowDateRange';
import CohortRowSubject from './CohortRowSubject';
import BuilderNamedCohort from '../BuilderNamedCohort';
import CohortPercentileFilter from '../CohortPercentileFilter';
import { useShowCohortPercentileFilter } from '../utils';
import { SimpleCohortTypeKey } from '../../../../../utils/apollo/graphqlSchema';
import BuilderTimeWindow from '../BuilderTimeWindow';
import CohortRowSamplePercentage from '../CohortRowSamplePercentage';
import ThinDivider from '../../../../ThinDivider';
import { CohortRowOperators } from '../CohortRowOperators';

const Container = styled.div`
  display: flex;
  min-height: 28px;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 16px;
  row-gap: 8px;
`;

interface IProps {
  cohortRow: ICohortRow;
  rowMeta: IRowMeta;
  onDeactivateHighlight: () => void;
  displayCohortData?: boolean;
  children?: any;
}

const CohortRowHeader = ({
  children,
  cohortRow,
  rowMeta,
  displayCohortData,
  onDeactivateHighlight,
}: IProps) => {
  const { simpleCohortTypeKey, isActive } = cohortRow;

  const notCohortSamplePage = rowMeta.rowType !== RowType.builderCohorts;

  const showPercentileFilter = useShowCohortPercentileFilter(cohortRow);

  const getTypeSpecificFields = () => {
    if (
      simpleCohortTypeKey === SimpleCohortTypeKey.Measure ||
      simpleCohortTypeKey === SimpleCohortTypeKey.Performed
    ) {
      return (
        <>
          <ThinDivider layout="vertical" size="short" />
          <CohortRowOperators />
          <ThinDivider layout="vertical" size="short" />
          <CohortRowDateRange rowMeta={rowMeta} cohortRow={cohortRow} />
          <ThinDivider layout="vertical" size="short" />
          <BuilderTimeWindow />
        </>
      );
    }

    return (
      <>
        <ThinDivider layout="vertical" size="short" />
        <CohortRowDateRange rowMeta={rowMeta} cohortRow={cohortRow} />
      </>
    );
  };

  return (
    <Container>
      <Content>
        <CohortRowTitle rowMeta={rowMeta} isActive={isActive} />
        <ThinDivider layout="vertical" size="short" />
        <BuilderNamedCohort
          rowMeta={rowMeta}
          onDeactivateHighlight={onDeactivateHighlight}
        />
        {showPercentileFilter && (
          <>
            <ThinDivider layout="vertical" size="short" />
            <div>
              <CohortPercentileFilter rowMeta={rowMeta} cohortRow={cohortRow} />
            </div>
          </>
        )}
        {displayCohortData && (
          <>
            {cohortRow.subject && (
              <>
                <ThinDivider layout="vertical" size="short" />
                <CohortRowSubject cohortRow={cohortRow} rowMeta={rowMeta} />
              </>
            )}
            <ThinDivider layout="vertical" size="short" />
            <CohortRowSimpleTypeKey cohortRow={cohortRow} rowMeta={rowMeta} />
            {getTypeSpecificFields()}
            {notCohortSamplePage && (
              <>
                <ThinDivider layout="vertical" size="short" />
                <CohortRowSamplePercentage rowMeta={rowMeta} />
              </>
            )}
          </>
        )}
      </Content>
      {children}
    </Container>
  );
};

export default CohortRowHeader;
